/**
 * 🔥 ROAST SHARE PAGE
 * ═══════════════════════════════════════════════════════════════
 * 
 * Displays a shareable roast result with all 7 god insights.
 * Used for viral sharing and SEO.
 * 
 * Route: /roast/:roastId
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Loader2, ExternalLink, Copy, Check, Share2, 
  ArrowLeft, Flame, AlertTriangle, TrendingUp,
  Twitter, Linkedin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// God emoji mapping
const GOD_EMOJIS = {
  'Plutus': '💰',
  'Athena': '🦉',
  'Eris': '⚔️',
  'Hermes': '📜',
  'Nike': '🏆',
  'Hestia': '🏠',
  'Apollo': '☀️',
};

const GOD_COLORS = {
  'Plutus': 'from-yellow-500 to-amber-600',
  'Athena': 'from-blue-500 to-indigo-600',
  'Eris': 'from-red-500 to-rose-600',
  'Hermes': 'from-cyan-500 to-teal-600',
  'Nike': 'from-green-500 to-emerald-600',
  'Hestia': 'from-orange-500 to-red-500',
  'Apollo': 'from-yellow-400 to-orange-500',
};

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-blue-400';
  if (score >= 40) return 'text-yellow-400';
  return 'text-red-400';
};

const getScoreBg = (score) => {
  if (score >= 80) return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
  if (score >= 60) return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
  if (score >= 40) return 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
  return 'from-red-500/20 to-rose-500/20 border-red-500/30';
};

const RoastSharePage = () => {
  const { roastId } = useParams();
  const [roastData, setRoastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchRoast = async () => {
      try {
        const response = await fetch(`${API_URL}/api/roast/${roastId}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Roast not found');
          }
          throw new Error('Failed to load roast');
        }
        const data = await response.json();
        setRoastData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRoast();
  }, [roastId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareTwitter = () => {
    const text = `🔥 ${roastData.url} just got roasted by 7 AI gods!\n\nScore: ${roastData.score}/100\n\nGet your site roasted free:`;
    const url = window.location.href;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareLinkedIn = () => {
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading roast results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Roast Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
          >
            <Flame className="w-5 h-5" />
            Get Your Own Roast
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">iSupplyAI</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleShareTwitter}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Share on Twitter"
            >
              <Twitter className="w-5 h-5 text-[#1DA1F2]" />
            </button>
            <button
              onClick={handleShareLinkedIn}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Share on LinkedIn"
            >
              <Linkedin className="w-5 h-5 text-[#0A66C2]" />
            </button>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Score Header */}
        <div className={`rounded-2xl border bg-gradient-to-br ${getScoreBg(roastData.score)} p-8 mb-8`}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Score Circle */}
            <div className="relative">
              <div className={`w-32 h-32 rounded-full border-4 ${roastData.score >= 60 ? 'border-green-500' : roastData.score >= 40 ? 'border-yellow-500' : 'border-red-500'} flex items-center justify-center bg-gray-900/50`}>
                <span className={`text-5xl font-bold ${getScoreColor(roastData.score)}`}>
                  {roastData.score}
                </span>
              </div>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs text-gray-400">
                /100
              </span>
            </div>

            {/* Site Info */}
            <div className="text-center md:text-left flex-1">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Flame className="w-6 h-6 text-orange-500" />
                <span className="text-orange-400 font-semibold uppercase tracking-wider text-sm">Website Roast</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 break-all">
                {roastData.url}
              </h1>
              <p className="text-gray-400">
                Roasted by <span className="text-white font-semibold">7 AI Gods</span>
              </p>
              {roastData.summary && (
                <p className="mt-4 text-lg text-gray-300 italic">"{roastData.summary}"</p>
              )}
            </div>

            {/* CTA */}
            <div className="flex flex-col gap-3">
              <a
                href={roastData.url.startsWith('http') ? roastData.url : `https://${roastData.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                Visit Site
              </a>
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 rounded-lg transition-colors text-sm font-semibold"
              >
                <Flame className="w-4 h-4" />
                Roast Your Site
              </Link>
            </div>
          </div>
        </div>

        {/* God Roasts */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <span>🏛️</span> The Divine Verdict
          </h2>
          
          <div className="grid gap-4">
            {roastData.roasts?.map((roast, index) => (
              <div 
                key={index}
                className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 hover:border-gray-600 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* God Avatar */}
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${GOD_COLORS[roast.name] || GOD_COLORS[roast.god] || 'from-purple-500 to-pink-500'} flex items-center justify-center text-2xl flex-shrink-0`}>
                    {roast.emoji || GOD_EMOJIS[roast.name] || GOD_EMOJIS[roast.god] || '⚡'}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-lg">{roast.name || roast.god}</h3>
                      <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-700 rounded-full">
                        {roast.focus || roast.category || 'Strategy'}
                      </span>
                    </div>
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {roast.content || roast.roast || roast.message || 'No insight available'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Want to know how YOUR site stacks up?</h2>
          <p className="text-gray-400 mb-6">Get roasted by 7 AI gods in seconds - completely free, no signup required.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
          >
            <Flame className="w-6 h-6" />
            Get Your Free Roast
          </Link>
        </div>

        {/* Share Footer */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <span className="text-gray-500 text-sm">Share this roast:</span>
          <button
            onClick={handleShareTwitter}
            className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 text-[#1DA1F2] rounded-lg transition-colors text-sm"
          >
            <Twitter className="w-4 h-4" />
            Twitter
          </button>
          <button
            onClick={handleShareLinkedIn}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 text-[#0A66C2] rounded-lg transition-colors text-sm"
          >
            <Linkedin className="w-4 h-4" />
            LinkedIn
          </button>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-sm"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Share2 className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-gray-500 text-sm">
          <p>Powered by <Link to="/" className="text-orange-400 hover:text-orange-300">iSupplyAI</Link> - AI Agents That Argue Your Strategy</p>
        </div>
      </footer>
    </div>
  );
};

export default RoastSharePage;
